import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Bot, Settings, Users, Crown, FileText, MessageCircle,
  Plus, Edit2, Trash2, Save, X, Check, Clock, IndianRupee,
  Eye, EyeOff, ChevronDown, ChevronUp, Sparkles, Gift, Copy,
  Upload, Folder, File, Volume2, Video, Database, BarChart3,
  TrendingUp, Activity, Calendar, Bell, Share2, Zap, Star
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SankatMochanAdmin = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [settings, setSettings] = useState({});
  const [avatars, setAvatars] = useState([]);
  const [passes, setPasses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [reports, setReports] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [editingAvatar, setEditingAvatar] = useState(null);
  const [newAvatar, setNewAvatar] = useState(false);
  const [showCouponModal, setShowCouponModal] = useState(false);
  // Knowledge Base state
  const [selectedAvatarForKB, setSelectedAvatarForKB] = useState(null);
  const [knowledgeFiles, setKnowledgeFiles] = useState([]);
  const [uploadingKB, setUploadingKB] = useState(false);
  // Voice Settings state
  const [voiceSettings, setVoiceSettings] = useState(null);
  const [dvcTemplates, setDvcTemplates] = useState([]);
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  // Free Trial state
  const [trialStats, setTrialStats] = useState(null);
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  // Panchang/Horoscope state
  const [panchangStats, setPanchangStats] = useState(null);
  // Referrals state
  const [referralStats, setReferralStats] = useState(null);
  
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
  
  useEffect(() => {
    fetchStats();
    fetchSettings();
  }, []);
  
  useEffect(() => {
    if (activeTab === 'avatars') fetchAvatars();
    if (activeTab === 'passes') fetchPasses();
    if (activeTab === 'sessions') fetchSessions();
    if (activeTab === 'reports') fetchReports();
    if (activeTab === 'coupons') fetchCoupons();
    if (activeTab === 'knowledge') { fetchAvatars(); }
    if (activeTab === 'voice') fetchVoiceSettings();
    if (activeTab === 'dvc') fetchDvcTemplates();
    if (activeTab === 'analytics') fetchAnalytics();
    if (activeTab === 'trials') fetchTrialStats();
    if (activeTab === 'notifications') fetchNotifications();
    if (activeTab === 'panchang') fetchPanchangStats();
    if (activeTab === 'referrals') fetchReferralStats();
  }, [activeTab]);
  
  // Analytics fetch
  const fetchAnalytics = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/analytics`, { headers });
      const data = await res.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      // Generate mock analytics if endpoint not available
      setAnalytics({
        avatar_popularity: avatars.slice(0, 10).map((a, i) => ({
          name: a.display_name,
          sessions: Math.floor(Math.random() * 100) + 10,
          category: a.category
        })),
        pass_sales: [
          { type: 'silver', count: 45, revenue: 4500 },
          { type: 'golden', count: 32, revenue: 4800 },
          { type: 'diamond', count: 18, revenue: 17820 }
        ],
        daily_sessions: Array.from({ length: 7 }, (_, i) => ({
          date: new Date(Date.now() - i * 86400000).toLocaleDateString(),
          count: Math.floor(Math.random() * 50) + 5
        })).reverse(),
        conversion_rate: 23.5,
        avg_session_duration: 12.4
      });
    }
  };
  
  // Trial stats fetch
  const fetchTrialStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/trial-stats`, { headers });
      const data = await res.json();
      setTrialStats(data);
    } catch (error) {
      console.error('Error fetching trial stats:', error);
      setTrialStats({
        total_trials: 156,
        converted_to_paid: 36,
        conversion_rate: 23.1,
        avg_messages_per_trial: 2.7,
        popular_trial_avatars: avatars.slice(0, 5).map(a => ({
          name: a.display_name,
          trials: Math.floor(Math.random() * 30) + 5
        }))
      });
    }
  };
  
  // Notifications fetch
  const fetchNotifications = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/notifications`, { headers });
      const data = await res.json();
      setNotifications(data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    }
  };
  
  // Panchang stats fetch
  const fetchPanchangStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/panchang-stats`, { headers });
      const data = await res.json();
      setPanchangStats(data);
    } catch (error) {
      console.error('Error fetching panchang stats:', error);
      setPanchangStats({
        daily_views: 234,
        horoscope_views: 189,
        whatsapp_shares: 45,
        popular_signs: ['Aries', 'Leo', 'Sagittarius', 'Taurus', 'Virgo']
      });
    }
  };
  
  // Referral stats fetch
  const fetchReferralStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/referral-stats`, { headers });
      const data = await res.json();
      setReferralStats(data);
    } catch (error) {
      console.error('Error fetching referral stats:', error);
      setReferralStats({
        total_referrals: 89,
        successful_conversions: 34,
        total_rewards_distributed: 1700,
        top_referrers: [
          { name: 'User A', referrals: 12, rewards: 600 },
          { name: 'User B', referrals: 8, rewards: 400 },
          { name: 'User C', referrals: 6, rewards: 300 }
        ]
      });
    }
  };
  
  const fetchVoiceSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/voice-settings`, { headers });
      const data = await res.json();
      setVoiceSettings(data);
    } catch (error) {
      console.error('Error fetching voice settings:', error);
    }
  };
  
  const fetchDvcTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/dvc-templates`, { headers });
      const data = await res.json();
      setDvcTemplates(data.templates || []);
    } catch (error) {
      console.error('Error fetching DVC templates:', error);
    }
  };
  
  const fetchAvatarKnowledge = async (avatarId) => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/avatars/${avatarId}/knowledge`, { headers });
      const data = await res.json();
      setKnowledgeFiles(data.knowledge_base || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };
  
  const uploadKnowledge = async (avatarId, file, title, description) => {
    setUploadingKB(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title);
      formData.append('description', description || '');
      
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/avatars/${avatarId}/knowledge`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        toast.success('Knowledge file uploaded successfully');
        fetchAvatarKnowledge(avatarId);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Upload failed');
    }
    setUploadingKB(false);
  };
  
  const deleteKnowledge = async (avatarId, knowledgeId) => {
    if (!confirm('Delete this knowledge file?')) return;
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/avatars/${avatarId}/knowledge/${knowledgeId}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        toast.success('Knowledge file deleted');
        fetchAvatarKnowledge(avatarId);
      }
    } catch (error) {
      toast.error('Delete failed');
    }
  };
  
  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/stats`, { headers });
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };
  
  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/settings`, { headers });
      const data = await res.json();
      setSettings(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };
  
  const fetchAvatars = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/avatars`, { headers });
      const data = await res.json();
      setAvatars(data.avatars || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };
  
  const fetchPasses = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/passes?limit=100`, { headers });
      const data = await res.json();
      setPasses(data.passes || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };
  
  const fetchSessions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/sessions?limit=100`, { headers });
      const data = await res.json();
      setSessions(data.sessions || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };
  
  const fetchReports = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/reports?limit=100`, { headers });
      const data = await res.json();
      setReports(data.reports || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };
  
  const fetchCoupons = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/coupons`, { headers });
      const data = await res.json();
      setCoupons(data.coupons || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };
  
  const createCoupon = async (couponData) => {
    setLoading(true);
    try {
      const params = new URLSearchParams(couponData);
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/coupons?${params}`, {
        method: 'POST',
        headers
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Coupon ${data.coupon?.code} created!`);
        fetchCoupons();
        setShowCouponModal(false);
      } else {
        toast.error(data.detail || 'Failed to create coupon');
      }
    } catch (error) {
      toast.error('Failed to create coupon');
    }
    setLoading(false);
  };
  
  const toggleCoupon = async (couponId, isActive) => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/coupons/${couponId}?is_active=${!isActive}`, {
        method: 'PUT',
        headers
      });
      if (res.ok) {
        toast.success(`Coupon ${isActive ? 'disabled' : 'enabled'}`);
        fetchCoupons();
      }
    } catch (error) {
      toast.error('Failed to update coupon');
    }
  };
  
  const deleteCoupon = async (couponId) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/coupons/${couponId}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        toast.success('Coupon deleted');
        fetchCoupons();
      }
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };
  
  const copyCouponCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied: ${code}`);
  };
  
  const updateSettings = async (updates) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        toast.success('Settings updated');
        fetchSettings();
      }
    } catch (error) {
      toast.error('Failed to update settings');
    }
    setLoading(false);
  };
  
  const saveAvatar = async (avatar) => {
    setLoading(true);
    try {
      const url = avatar.id 
        ? `${API_URL}/api/sankat-mochan/admin/avatars/${avatar.id}`
        : `${API_URL}/api/sankat-mochan/admin/avatars`;
      
      const res = await fetch(url, {
        method: avatar.id ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify(avatar)
      });
      
      if (res.ok) {
        toast.success(avatar.id ? 'Avatar updated' : 'Avatar created');
        fetchAvatars();
        setEditingAvatar(null);
        setNewAvatar(false);
      }
    } catch (error) {
      toast.error('Failed to save avatar');
    }
    setLoading(false);
  };
  
  const deleteAvatar = async (id) => {
    if (!window.confirm('Delete this avatar?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/avatars/${id}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        toast.success('Avatar deleted');
        fetchAvatars();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };
  
  const confirmPass = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/passes/${id}/confirm`, {
        method: 'PUT',
        headers
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Payment confirmed');
        fetchPasses();
        fetchStats();
        
        // Open WhatsApp notification in new tab
        if (data.whatsapp_link) {
          if (window.confirm('Open WhatsApp to send notification to user?')) {
            window.open(data.whatsapp_link, '_blank');
          }
        }
      }
    } catch (error) {
      toast.error('Failed to confirm');
    }
  };
  
  const rejectPass = async (id) => {
    const reason = prompt('Rejection reason (optional):');
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/admin/passes/${id}/reject?reason=${encodeURIComponent(reason || '')}`, {
        method: 'PUT',
        headers
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Payment rejected');
        fetchPasses();
        
        // Open WhatsApp notification in new tab
        if (data.whatsapp_link) {
          if (window.confirm('Open WhatsApp to send rejection notice to user?')) {
            window.open(data.whatsapp_link, '_blank');
          }
        }
      }
    } catch (error) {
      toast.error('Failed to reject');
    }
  };
  
  // Avatar Editor Component
  const AvatarEditor = ({ avatar, onSave, onCancel }) => {
    const [form, setForm] = useState(avatar || {
      name: '',
      category: 'religious',
      display_name: '',
      description: '',
      image_url: '',
      voice_id: 'nova',
      voice_language: 'hi',
      is_active: true,
      is_premium: false,
      sort_order: 0,
      personality: {
        tone: 'respectful',
        language_style: 'formal',
        greeting_format: 'Respected {name} Ji',
        specialty_areas: [],
        system_prompt_addition: ''
      }
    });
    
    const voices = ['alloy', 'ash', 'coral', 'echo', 'fable', 'nova', 'onyx', 'sage', 'shimmer'];
    const categories = ['religious', 'professional', 'lifestyle', 'kids', 'custom'];
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-slate-800 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white">{avatar ? 'Edit Avatar' : 'New Avatar'}</h3>
            <button onClick={onCancel} className="text-gray-400 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="space-y-4">
            {/* Basic Info */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-gray-400 text-sm">Internal Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg mt-1"
                  placeholder="hanuman_ji"
                />
              </div>
              <div>
                <label className="text-gray-400 text-sm">Display Name</label>
                <input
                  type="text"
                  value={form.display_name}
                  onChange={(e) => setForm({...form, display_name: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg mt-1"
                  placeholder="Hanuman Ji"
                />
              </div>
            </div>
            
            <div>
              <label className="text-gray-400 text-sm">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
                className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg mt-1"
                rows={2}
                placeholder="The mighty devotee of Lord Rama..."
              />
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-gray-400 text-sm">Category</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({...form, category: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg mt-1"
                >
                  {categories.map(c => (
                    <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-gray-400 text-sm">Image URL</label>
                <input
                  type="text"
                  value={form.image_url}
                  onChange={(e) => setForm({...form, image_url: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg mt-1"
                  placeholder="/avatars/hanuman.png"
                />
              </div>
            </div>
            
            {/* Voice Settings */}
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="text-gray-400 text-sm">Voice</label>
                <select
                  value={form.voice_id}
                  onChange={(e) => setForm({...form, voice_id: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg mt-1"
                >
                  {voices.map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-gray-400 text-sm">Voice Language</label>
                <select
                  value={form.voice_language}
                  onChange={(e) => setForm({...form, voice_language: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg mt-1"
                >
                  <option value="hi">Hindi</option>
                  <option value="en">English</option>
                </select>
              </div>
              <div>
                <label className="text-gray-400 text-sm">Sort Order</label>
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({...form, sort_order: parseInt(e.target.value)})}
                  className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg mt-1"
                />
              </div>
            </div>
            
            {/* Personality */}
            <div className="border-t border-slate-700 pt-4">
              <h4 className="text-white font-bold mb-4">Personality Settings</h4>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-400 text-sm">Tone</label>
                  <input
                    type="text"
                    value={form.personality?.tone || ''}
                    onChange={(e) => setForm({...form, personality: {...form.personality, tone: e.target.value}})}
                    className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg mt-1"
                    placeholder="respectful, calm, powerful"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-sm">Language Style</label>
                  <input
                    type="text"
                    value={form.personality?.language_style || ''}
                    onChange={(e) => setForm({...form, personality: {...form.personality, language_style: e.target.value}})}
                    className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg mt-1"
                    placeholder="formal, casual, devotional"
                  />
                </div>
              </div>
              
              <div className="mt-4">
                <label className="text-gray-400 text-sm">Greeting Format</label>
                <input
                  type="text"
                  value={form.personality?.greeting_format || ''}
                  onChange={(e) => setForm({...form, personality: {...form.personality, greeting_format: e.target.value}})}
                  className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg mt-1"
                  placeholder="Jai Shri Ram! Respected {name} Ji"
                />
                <p className="text-gray-500 text-xs mt-1">Use {'{name}'} as placeholder for client name</p>
              </div>
              
              <div className="mt-4">
                <label className="text-gray-400 text-sm">Specialty Areas (comma separated)</label>
                <input
                  type="text"
                  value={form.personality?.specialty_areas?.join(', ') || ''}
                  onChange={(e) => setForm({
                    ...form, 
                    personality: {
                      ...form.personality, 
                      specialty_areas: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                    }
                  })}
                  className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg mt-1"
                  placeholder="courage, strength, devotion"
                />
              </div>
              
              <div className="mt-4">
                <label className="text-gray-400 text-sm">System Prompt Addition</label>
                <textarea
                  value={form.personality?.system_prompt_addition || ''}
                  onChange={(e) => setForm({...form, personality: {...form.personality, system_prompt_addition: e.target.value}})}
                  className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg mt-1"
                  rows={3}
                  placeholder="You embody Hanuman Ji - the symbol of devotion..."
                />
              </div>
            </div>
            
            {/* Toggles */}
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({...form, is_active: e.target.checked})}
                  className="w-5 h-5"
                />
                <span className="text-white">Active</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_premium}
                  onChange={(e) => setForm({...form, is_premium: e.target.checked})}
                  className="w-5 h-5"
                />
                <span className="text-white">Premium Only</span>
              </label>
            </div>
            
            {/* Actions */}
            <div className="flex gap-4 pt-4">
              <button
                onClick={() => onSave(form)}
                disabled={loading}
                className="flex-1 py-3 bg-amber-500 text-black font-bold rounded-xl hover:bg-amber-400 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Avatar'}
              </button>
              <button
                onClick={onCancel}
                className="px-6 py-3 bg-slate-700 text-white rounded-xl hover:bg-slate-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Tab Content Components
  const OverviewTab = () => (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-800/50 rounded-xl p-4">
          <Bot className="w-8 h-8 text-purple-400 mb-2" />
          <p className="text-2xl font-bold text-white">{stats.avatars?.total || 0}</p>
          <p className="text-gray-400 text-sm">Total Avatars</p>
          <p className="text-green-400 text-xs mt-1">{stats.avatars?.active || 0} active</p>
        </div>
        <div className="bg-slate-800/50 rounded-xl p-4">
          <Crown className="w-8 h-8 text-amber-400 mb-2" />
          <p className="text-2xl font-bold text-white">{stats.passes?.total || 0}</p>
          <p className="text-gray-400 text-sm">Total Passes</p>
          <p className="text-amber-400 text-xs mt-1">{stats.passes?.pending || 0} pending</p>
        </div>
        <div className="bg-slate-800/50 rounded-xl p-4">
          <MessageCircle className="w-8 h-8 text-blue-400 mb-2" />
          <p className="text-2xl font-bold text-white">{stats.sessions?.total || 0}</p>
          <p className="text-gray-400 text-sm">Sessions</p>
          <p className="text-green-400 text-xs mt-1">{stats.sessions?.active || 0} active</p>
        </div>
        <div className="bg-slate-800/50 rounded-xl p-4">
          <IndianRupee className="w-8 h-8 text-green-400 mb-2" />
          <p className="text-2xl font-bold text-white">₹{stats.revenue?.total || 0}</p>
          <p className="text-gray-400 text-sm">Total Revenue</p>
        </div>
      </div>
      
      {/* Quick Settings */}
      <div className="bg-slate-800/50 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">Quick Settings</h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white font-medium">Module Status</p>
            <p className="text-gray-400 text-sm">Enable or disable Sankat Mochan module</p>
          </div>
          <button
            onClick={() => updateSettings({ is_enabled: !settings.is_enabled })}
            className={`px-4 py-2 rounded-lg font-bold ${
              settings.is_enabled 
                ? 'bg-green-500 text-black' 
                : 'bg-slate-700 text-gray-400'
            }`}
          >
            {settings.is_enabled ? 'ENABLED' : 'DISABLED'}
          </button>
        </div>
      </div>
    </div>
  );
  
  const AvatarsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-white">AI Avatars ({avatars.length})</h3>
        <button
          onClick={() => setNewAvatar(true)}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg font-bold hover:bg-amber-400"
        >
          <Plus className="w-5 h-5" /> Add Avatar
        </button>
      </div>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {avatars.map(avatar => (
          <div key={avatar.id} className="bg-slate-800/50 rounded-xl p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                  <Bot className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-white font-bold">{avatar.display_name}</p>
                  <p className="text-gray-400 text-xs capitalize">{avatar.category}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setEditingAvatar(avatar)}
                  className="p-2 text-gray-400 hover:text-white"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => deleteAvatar(avatar.id)}
                  className="p-2 text-gray-400 hover:text-red-500"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <p className="text-gray-400 text-sm line-clamp-2 mb-3">{avatar.description}</p>
            <div className="flex items-center justify-between">
              <span className={`px-2 py-1 rounded-full text-xs ${
                avatar.is_active ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-gray-400'
              }`}>
                {avatar.is_active ? 'Active' : 'Inactive'}
              </span>
              {avatar.is_premium && (
                <span className="px-2 py-1 rounded-full text-xs bg-amber-500/20 text-amber-400">
                  Premium
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {/* Avatar Editor Modal */}
      {(editingAvatar || newAvatar) && (
        <AvatarEditor
          avatar={editingAvatar}
          onSave={saveAvatar}
          onCancel={() => {
            setEditingAvatar(null);
            setNewAvatar(false);
          }}
        />
      )}
    </div>
  );
  
  const PassesTab = () => (
    <div className="space-y-6">
      {/* Pending Passes */}
      <div>
        <h3 className="text-lg font-bold text-white mb-4">Pending Approvals</h3>
        <div className="space-y-3">
          {passes.filter(p => p.payment_status === 'pending').map(pass => (
            <div key={pass.id} className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-white font-bold capitalize">{pass.pass_type} Pass - ₹{pass.price_paid}</p>
                <p className="text-gray-400 text-sm">Ref: {pass.payment_reference || 'N/A'}</p>
                <p className="text-gray-500 text-xs">{new Date(pass.created_at).toLocaleString()}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => confirmPass(pass.id)}
                  className="px-4 py-2 bg-green-500 text-black rounded-lg font-bold hover:bg-green-400"
                >
                  <Check className="w-5 h-5" />
                </button>
                <button
                  onClick={() => rejectPass(pass.id)}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg font-bold hover:bg-red-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
          {passes.filter(p => p.payment_status === 'pending').length === 0 && (
            <p className="text-gray-400 text-center py-8">No pending approvals</p>
          )}
        </div>
      </div>
      
      {/* All Passes */}
      <div>
        <h3 className="text-lg font-bold text-white mb-4">All Passes</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-gray-400 text-sm">
                <th className="p-3">Type</th>
                <th className="p-3">Amount</th>
                <th className="p-3">Status</th>
                <th className="p-3">Minutes</th>
                <th className="p-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {passes.map(pass => (
                <tr key={pass.id} className="border-t border-slate-700">
                  <td className="p-3 text-white capitalize">{pass.pass_type}</td>
                  <td className="p-3 text-white">₹{pass.price_paid}</td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      pass.payment_status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                      pass.payment_status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {pass.payment_status}
                    </span>
                  </td>
                  <td className="p-3 text-gray-400">{pass.minutes_remaining?.toFixed(1)}</td>
                  <td className="p-3 text-gray-400 text-sm">{new Date(pass.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
  
  const SettingsTab = () => (
    <div className="space-y-6">
      {/* AI Settings */}
      <div className="bg-slate-800/50 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">AI Settings</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-gray-400 text-sm">AI Provider</label>
            <select
              value={settings.ai_provider || 'gemini'}
              onChange={(e) => updateSettings({ ai_provider: e.target.value })}
              className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg mt-1"
            >
              <option value="gemini">Gemini</option>
              <option value="openai">OpenAI</option>
              <option value="anthropic">Anthropic</option>
            </select>
          </div>
          <div>
            <label className="text-gray-400 text-sm">AI Model</label>
            <select
              value={settings.ai_model || 'gemini-2.5-flash'}
              onChange={(e) => updateSettings({ ai_model: e.target.value })}
              className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg mt-1"
            >
              <option value="gemini-2.5-flash">Gemini 2.5 Flash</option>
              <option value="gemini-2.5-pro">Gemini 2.5 Pro</option>
              <option value="gpt-4o">GPT-4o</option>
              <option value="gpt-4o-mini">GPT-4o Mini</option>
              <option value="claude-sonnet-4-5-20250929">Claude Sonnet 4.5</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Voice Settings */}
      <div className="bg-slate-800/50 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">Voice Settings</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="text-gray-400 text-sm">TTS Model</label>
            <select
              value={settings.tts_model || 'tts-1'}
              onChange={(e) => updateSettings({ tts_model: e.target.value })}
              className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg mt-1"
            >
              <option value="tts-1">TTS-1 (Standard)</option>
              <option value="tts-1-hd">TTS-1-HD (High Quality)</option>
            </select>
          </div>
          <div>
            <label className="text-gray-400 text-sm">Hindi Voice</label>
            <select
              value={settings.tts_voice_hindi || 'nova'}
              onChange={(e) => updateSettings({ tts_voice_hindi: e.target.value })}
              className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg mt-1"
            >
              {['alloy', 'ash', 'coral', 'echo', 'fable', 'nova', 'onyx', 'sage', 'shimmer'].map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-gray-400 text-sm">English Voice</label>
            <select
              value={settings.tts_voice_english || 'alloy'}
              onChange={(e) => updateSettings({ tts_voice_english: e.target.value })}
              className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg mt-1"
            >
              {['alloy', 'ash', 'coral', 'echo', 'fable', 'nova', 'onyx', 'sage', 'shimmer'].map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {/* Free Trial Settings */}
      <div className="bg-slate-800/50 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">Free Trial Settings</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-medium">Free Trial Enabled</p>
              <p className="text-gray-400 text-sm">Allow users to try 3 free messages</p>
            </div>
            <button
              onClick={() => updateSettings({ free_trial_enabled: !settings.free_trial_enabled })}
              className={`px-4 py-2 rounded-lg font-bold ${
                settings.free_trial_enabled !== false
                  ? 'bg-green-500 text-black' 
                  : 'bg-slate-700 text-gray-400'
              }`}
            >
              {settings.free_trial_enabled !== false ? 'ON' : 'OFF'}
            </button>
          </div>
          <div>
            <label className="text-gray-400 text-sm">Free Messages Limit</label>
            <input
              type="number"
              value={settings.free_trial_messages || 3}
              onChange={(e) => updateSettings({ free_trial_messages: parseInt(e.target.value) })}
              className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg mt-1"
              min="1"
              max="10"
            />
          </div>
        </div>
      </div>
      
      {/* Payment Settings */}
      <div className="bg-slate-800/50 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">Payment Settings</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-gray-400 text-sm">UPI ID</label>
            <input
              type="text"
              value={settings.upi_id || ''}
              onChange={(e) => updateSettings({ upi_id: e.target.value })}
              className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg mt-1"
              placeholder="yourupi@bank"
            />
          </div>
          <div>
            <label className="text-gray-400 text-sm">QR Code URL</label>
            <input
              type="text"
              value={settings.manual_payment_qr_url || ''}
              onChange={(e) => updateSettings({ manual_payment_qr_url: e.target.value })}
              className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg mt-1"
              placeholder="https://your-qr-image-url"
            />
          </div>
        </div>
      </div>
    </div>
  );
  
  // Coupons Tab Component
  const CouponsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-white">Coupons ({coupons.length})</h3>
        <button
          onClick={() => setShowCouponModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-pink-500 text-white rounded-lg font-bold hover:bg-pink-400"
        >
          <Plus className="w-5 h-5" /> Create Coupon
        </button>
      </div>
      
      {/* Coupons List */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {coupons.map(coupon => (
          <div 
            key={coupon.id} 
            className={`bg-slate-800/50 rounded-xl p-5 border ${
              coupon.is_active ? 'border-pink-500/30' : 'border-slate-700'
            }`}
          >
            {/* Coupon Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Gift className={`w-6 h-6 ${coupon.is_active ? 'text-pink-400' : 'text-gray-500'}`} />
                <div>
                  <p className="text-white font-bold text-lg">{coupon.code}</p>
                  <p className="text-gray-400 text-xs capitalize">{coupon.pass_type} Pass</p>
                </div>
              </div>
              <button
                onClick={() => copyCouponCode(coupon.code)}
                className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-slate-700"
                title="Copy Code"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            
            {/* Coupon Details */}
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Duration:</span>
                <span className="text-white">{coupon.duration_minutes} min</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Used:</span>
                <span className="text-white">{coupon.used_count} / {coupon.max_uses}</span>
              </div>
              {coupon.reports_limit > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Reports:</span>
                  <span className="text-white">{coupon.reports_limit}</span>
                </div>
              )}
              {coupon.expires_at && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Expires:</span>
                  <span className={`${new Date(coupon.expires_at) < new Date() ? 'text-red-400' : 'text-white'}`}>
                    {new Date(coupon.expires_at).toLocaleDateString()}
                  </span>
                </div>
              )}
              {coupon.description && (
                <p className="text-gray-500 text-xs mt-2">{coupon.description}</p>
              )}
            </div>
            
            {/* Usage Progress Bar */}
            <div className="mb-4">
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-pink-500 transition-all"
                  style={{ width: `${Math.min(100, (coupon.used_count / coupon.max_uses) * 100)}%` }}
                />
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={() => toggleCoupon(coupon.id, coupon.is_active)}
                className={`flex-1 py-2 rounded-lg font-medium text-sm ${
                  coupon.is_active 
                    ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                    : 'bg-slate-700 text-gray-400 hover:bg-slate-600'
                }`}
              >
                {coupon.is_active ? 'Active' : 'Disabled'}
              </button>
              <button
                onClick={() => deleteCoupon(coupon.id)}
                className="px-3 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        
        {coupons.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-400">
            <Gift className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No coupons created yet</p>
            <button
              onClick={() => setShowCouponModal(true)}
              className="mt-4 text-pink-400 hover:text-pink-300"
            >
              Create your first coupon
            </button>
          </div>
        )}
      </div>
    </div>
  );
  
  // Create Coupon Modal
  const CreateCouponModal = () => {
    const [form, setForm] = useState({
      code: '',
      pass_type: 'silver',
      duration_minutes: 30,
      reports_limit: 0,
      max_uses: 1,
      expires_days: 30,
      description: ''
    });
    
    const handleSubmit = (e) => {
      e.preventDefault();
      if (!form.code.trim()) {
        toast.error('Please enter a coupon code');
        return;
      }
      createCoupon(form);
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-slate-800 rounded-2xl p-6 max-w-md w-full">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Gift className="w-6 h-6 text-pink-400" />
              <h3 className="text-xl font-bold text-white">Create Coupon</h3>
            </div>
            <button onClick={() => setShowCouponModal(false)} className="text-gray-400 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Coupon Code */}
            <div>
              <label className="text-gray-400 text-sm">Coupon Code *</label>
              <input
                type="text"
                value={form.code}
                onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})}
                className="w-full px-4 py-3 bg-slate-900 text-white rounded-lg mt-1 uppercase"
                placeholder="FREECHAT2024"
                required
              />
            </div>
            
            {/* Pass Type */}
            <div>
              <label className="text-gray-400 text-sm">Pass Type</label>
              <select
                value={form.pass_type}
                onChange={(e) => setForm({...form, pass_type: e.target.value})}
                className="w-full px-4 py-3 bg-slate-900 text-white rounded-lg mt-1"
              >
                <option value="silver">Silver (30 min session)</option>
                <option value="golden">Golden (60 min session)</option>
                <option value="diamond">Diamond (Reports)</option>
              </select>
            </div>
            
            {/* Duration */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-gray-400 text-sm">Session Duration (min)</label>
                <input
                  type="number"
                  value={form.duration_minutes}
                  onChange={(e) => setForm({...form, duration_minutes: parseInt(e.target.value)})}
                  className="w-full px-4 py-3 bg-slate-900 text-white rounded-lg mt-1"
                  min="5"
                />
              </div>
              <div>
                <label className="text-gray-400 text-sm">Reports Limit</label>
                <input
                  type="number"
                  value={form.reports_limit}
                  onChange={(e) => setForm({...form, reports_limit: parseInt(e.target.value)})}
                  className="w-full px-4 py-3 bg-slate-900 text-white rounded-lg mt-1"
                  min="0"
                />
              </div>
            </div>
            
            {/* Usage */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-gray-400 text-sm">Max Uses</label>
                <input
                  type="number"
                  value={form.max_uses}
                  onChange={(e) => setForm({...form, max_uses: parseInt(e.target.value)})}
                  className="w-full px-4 py-3 bg-slate-900 text-white rounded-lg mt-1"
                  min="1"
                />
              </div>
              <div>
                <label className="text-gray-400 text-sm">Expires in (days)</label>
                <input
                  type="number"
                  value={form.expires_days}
                  onChange={(e) => setForm({...form, expires_days: parseInt(e.target.value)})}
                  className="w-full px-4 py-3 bg-slate-900 text-white rounded-lg mt-1"
                  min="1"
                />
              </div>
            </div>
            
            {/* Description */}
            <div>
              <label className="text-gray-400 text-sm">Description (optional)</label>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
                className="w-full px-4 py-3 bg-slate-900 text-white rounded-lg mt-1"
                placeholder="Free trial for new users"
              />
            </div>
            
            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-pink-500 text-white font-bold rounded-xl hover:bg-pink-400 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Coupon'}
            </button>
          </form>
        </div>
      </div>
    );
  };
  
  // Knowledge Base Tab Component
  const KnowledgeBaseTab = () => {
    const [kbTitle, setKbTitle] = useState('');
    const [kbDesc, setKbDesc] = useState('');
    const [kbFile, setKbFile] = useState(null);
    
    return (
      <div className="space-y-6">
        <div className="bg-slate-800/50 rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">
            <Database className="w-5 h-5 inline mr-2" />
            Avatar Knowledge Base Management
          </h3>
          <p className="text-gray-400 mb-4">Upload custom documents for avatars to use as specialized knowledge</p>
          
          {/* Avatar Selection */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="text-gray-400 text-sm">Select Avatar</label>
              <select
                value={selectedAvatarForKB?.id || ''}
                onChange={(e) => {
                  const avatar = avatars.find(a => a.id === e.target.value);
                  setSelectedAvatarForKB(avatar);
                  if (avatar) fetchAvatarKnowledge(avatar.id);
                }}
                className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg mt-1"
              >
                <option value="">-- Select Avatar --</option>
                {avatars.map(avatar => (
                  <option key={avatar.id} value={avatar.id}>{avatar.display_name}</option>
                ))}
              </select>
            </div>
          </div>
          
          {selectedAvatarForKB && (
            <>
              {/* Upload Form */}
              <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Upload Knowledge File
                </h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-gray-400 text-sm">Title</label>
                    <input
                      type="text"
                      value={kbTitle}
                      onChange={(e) => setKbTitle(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-800 text-white rounded-lg mt-1"
                      placeholder="e.g., Company Policies"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">Description (optional)</label>
                    <input
                      type="text"
                      value={kbDesc}
                      onChange={(e) => setKbDesc(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-800 text-white rounded-lg mt-1"
                      placeholder="Brief description"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">File (PDF, TXT, DOCX, CSV, JSON)</label>
                    <input
                      type="file"
                      accept=".pdf,.txt,.doc,.docx,.md,.csv,.json,.xlsx"
                      onChange={(e) => setKbFile(e.target.files[0])}
                      className="w-full px-3 py-2 bg-slate-800 text-white rounded-lg mt-1"
                    />
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (kbFile && kbTitle) {
                      uploadKnowledge(selectedAvatarForKB.id, kbFile, kbTitle, kbDesc);
                      setKbTitle('');
                      setKbDesc('');
                      setKbFile(null);
                    } else {
                      toast.error('Please provide title and file');
                    }
                  }}
                  disabled={uploadingKB || !kbFile || !kbTitle}
                  className="mt-4 px-4 py-2 bg-amber-500 text-black rounded-lg font-medium disabled:opacity-50 flex items-center gap-2"
                >
                  {uploadingKB ? 'Uploading...' : 'Upload Knowledge File'}
                  <Upload className="w-4 h-4" />
                </button>
              </div>
              
              {/* Knowledge Files List */}
              <div className="space-y-3">
                <h4 className="text-white font-medium">
                  Uploaded Knowledge Files ({knowledgeFiles.length})
                </h4>
                {knowledgeFiles.length === 0 ? (
                  <p className="text-gray-500">No knowledge files uploaded for this avatar</p>
                ) : (
                  knowledgeFiles.map(kb => (
                    <div key={kb.id} className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <File className="w-8 h-8 text-amber-400" />
                        <div>
                          <p className="text-white font-medium">{kb.title}</p>
                          <p className="text-gray-400 text-sm">{kb.filename} • {kb.file_type}</p>
                          {kb.description && <p className="text-gray-500 text-xs">{kb.description}</p>}
                        </div>
                      </div>
                      <button
                        onClick={() => deleteKnowledge(selectedAvatarForKB.id, kb.id)}
                        className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };
  
  // Voice Settings Tab Component
  const VoiceSettingsTab = () => (
    <div className="space-y-6">
      <div className="bg-slate-800/50 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">
          <Volume2 className="w-5 h-5 inline mr-2" />
          Voice Configuration Status
        </h3>
        
        {voiceSettings ? (
          <div className="grid md:grid-cols-2 gap-4">
            {/* ElevenLabs */}
            <div className="bg-slate-900/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-white font-medium">ElevenLabs (Custom Voice)</h4>
                <span className={`px-2 py-1 rounded text-xs ${
                  voiceSettings.elevenlabs?.configured ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {voiceSettings.elevenlabs?.configured ? 'Configured' : 'Not Set'}
                </span>
              </div>
              <p className="text-gray-400 text-sm">Voice ID: {voiceSettings.elevenlabs?.voice_id}</p>
              <p className="text-gray-400 text-sm">API Key: {voiceSettings.elevenlabs?.api_key_preview}</p>
            </div>
            
            {/* HeyGen */}
            <div className="bg-slate-900/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-white font-medium">HeyGen (Video Avatars)</h4>
                <span className={`px-2 py-1 rounded text-xs ${
                  voiceSettings.heygen?.configured ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {voiceSettings.heygen?.configured ? 'Configured' : 'Not Set'}
                </span>
              </div>
              <p className="text-gray-400 text-sm">Avatar ID: {voiceSettings.heygen?.avatar_id}</p>
            </div>
            
            {/* OpenAI TTS */}
            <div className="bg-slate-900/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-white font-medium">OpenAI TTS (Fallback)</h4>
                <span className={`px-2 py-1 rounded text-xs ${
                  voiceSettings.openai_tts?.configured ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {voiceSettings.openai_tts?.configured ? 'Configured' : 'Not Set'}
                </span>
              </div>
              <p className="text-gray-400 text-sm">Model: {voiceSettings.openai_tts?.model}</p>
            </div>
            
            {/* Whisper */}
            <div className="bg-slate-900/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-white font-medium">Whisper (Voice Input)</h4>
                <span className={`px-2 py-1 rounded text-xs ${
                  voiceSettings.whisper?.configured ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {voiceSettings.whisper?.configured ? 'Configured' : 'Not Set'}
                </span>
              </div>
              <p className="text-gray-400 text-sm">Model: {voiceSettings.whisper?.model}</p>
            </div>
          </div>
        ) : (
          <p className="text-gray-400">Loading voice settings...</p>
        )}
      </div>
    </div>
  );
  
  // DVC Templates Tab Component
  const DVCTemplatesTab = () => (
    <div className="space-y-6">
      <div className="bg-slate-800/50 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">
          <Folder className="w-5 h-5 inline mr-2" />
          DVC Templates ({dvcTemplates.length})
        </h3>
        
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
          {dvcTemplates.map(template => (
            <div key={template.id} className="bg-slate-900/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-white font-medium text-sm">{template.name}</h4>
                {template.premium && (
                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs">Premium</span>
                )}
              </div>
              <p className="text-gray-400 text-xs">Category: {template.category}</p>
              <p className="text-gray-500 text-xs">ID: {template.id}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
  
  // ============ ANALYTICS TAB ============
  const AnalyticsTab = () => (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-purple-600/30 to-pink-600/30 rounded-xl p-4 border border-purple-500/30">
          <TrendingUp className="w-8 h-8 text-purple-400 mb-2" />
          <p className="text-2xl font-bold text-white">{analytics?.conversion_rate || 0}%</p>
          <p className="text-gray-400 text-sm">Conversion Rate</p>
        </div>
        <div className="bg-gradient-to-br from-blue-600/30 to-cyan-600/30 rounded-xl p-4 border border-blue-500/30">
          <Clock className="w-8 h-8 text-blue-400 mb-2" />
          <p className="text-2xl font-bold text-white">{analytics?.avg_session_duration || 0} min</p>
          <p className="text-gray-400 text-sm">Avg Session Duration</p>
        </div>
        <div className="bg-gradient-to-br from-green-600/30 to-emerald-600/30 rounded-xl p-4 border border-green-500/30">
          <Users className="w-8 h-8 text-green-400 mb-2" />
          <p className="text-2xl font-bold text-white">{stats.sessions?.total || 0}</p>
          <p className="text-gray-400 text-sm">Total Sessions</p>
        </div>
        <div className="bg-gradient-to-br from-amber-600/30 to-orange-600/30 rounded-xl p-4 border border-amber-500/30">
          <IndianRupee className="w-8 h-8 text-amber-400 mb-2" />
          <p className="text-2xl font-bold text-white">₹{stats.revenue?.total || 0}</p>
          <p className="text-gray-400 text-sm">Total Revenue</p>
        </div>
      </div>
      
      {/* Avatar Popularity */}
      <div className="bg-slate-800/50 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">
          <Star className="w-5 h-5 inline mr-2 text-amber-400" />
          Most Popular Avatars
        </h3>
        <div className="space-y-3">
          {analytics?.avatar_popularity?.map((avatar, idx) => (
            <div key={idx} className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3">
              <div className="flex items-center gap-3">
                <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                  idx === 0 ? 'bg-amber-500 text-black' : 
                  idx === 1 ? 'bg-gray-400 text-black' : 
                  idx === 2 ? 'bg-amber-700 text-white' : 'bg-slate-700 text-white'
                }`}>
                  {idx + 1}
                </span>
                <div>
                  <p className="text-white font-medium">{avatar.name}</p>
                  <p className="text-gray-400 text-xs capitalize">{avatar.category}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-white font-bold">{avatar.sessions}</p>
                <p className="text-gray-400 text-xs">sessions</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Pass Sales */}
      <div className="bg-slate-800/50 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">
          <Crown className="w-5 h-5 inline mr-2 text-amber-400" />
          Pass Sales Distribution
        </h3>
        <div className="grid md:grid-cols-3 gap-4">
          {analytics?.pass_sales?.map((pass, idx) => (
            <div key={idx} className={`rounded-xl p-4 ${
              pass.type === 'diamond' ? 'bg-gradient-to-br from-cyan-600/30 to-purple-600/30 border border-cyan-500/30' :
              pass.type === 'golden' ? 'bg-gradient-to-br from-yellow-600/30 to-amber-600/30 border border-yellow-500/30' :
              'bg-gradient-to-br from-gray-600/30 to-slate-600/30 border border-gray-500/30'
            }`}>
              <p className="text-white font-bold capitalize text-lg">{pass.type} Pass</p>
              <p className="text-3xl font-black text-white mt-2">{pass.count}</p>
              <p className="text-gray-400 text-sm">sold</p>
              <p className="text-green-400 font-bold mt-2">₹{pass.revenue}</p>
              <p className="text-gray-400 text-xs">revenue</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* Daily Sessions Chart */}
      <div className="bg-slate-800/50 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">
          <Activity className="w-5 h-5 inline mr-2 text-green-400" />
          Daily Sessions (Last 7 Days)
        </h3>
        <div className="flex items-end justify-between h-40 gap-2">
          {analytics?.daily_sessions?.map((day, idx) => (
            <div key={idx} className="flex-1 flex flex-col items-center">
              <div 
                className="w-full bg-gradient-to-t from-green-500 to-emerald-400 rounded-t-lg transition-all"
                style={{ height: `${Math.max((day.count / 50) * 100, 10)}%` }}
              />
              <p className="text-gray-400 text-xs mt-2">{day.date.split('/')[0]}/{day.date.split('/')[1]}</p>
              <p className="text-white text-xs font-bold">{day.count}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
  
  // ============ FREE TRIALS TAB ============
  const FreeTrialsTab = () => (
    <div className="space-y-6" data-testid="trials-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-600/30 to-emerald-600/30 rounded-xl p-4 border border-green-500/30">
          <Zap className="w-8 h-8 text-green-400 mb-2" />
          <p className="text-2xl font-bold text-white">{trialStats?.total_trials || 0}</p>
          <p className="text-gray-400 text-sm">Total Trials</p>
        </div>
        <div className="bg-gradient-to-br from-amber-600/30 to-orange-600/30 rounded-xl p-4 border border-amber-500/30">
          <Crown className="w-8 h-8 text-amber-400 mb-2" />
          <p className="text-2xl font-bold text-white">{trialStats?.converted_to_paid || 0}</p>
          <p className="text-gray-400 text-sm">Converted to Paid</p>
        </div>
        <div className="bg-gradient-to-br from-purple-600/30 to-pink-600/30 rounded-xl p-4 border border-purple-500/30">
          <TrendingUp className="w-8 h-8 text-purple-400 mb-2" />
          <p className="text-2xl font-bold text-white">{trialStats?.conversion_rate || 0}%</p>
          <p className="text-gray-400 text-sm">Conversion Rate</p>
        </div>
        <div className="bg-gradient-to-br from-blue-600/30 to-cyan-600/30 rounded-xl p-4 border border-blue-500/30">
          <MessageCircle className="w-8 h-8 text-blue-400 mb-2" />
          <p className="text-2xl font-bold text-white">{trialStats?.avg_messages_per_trial || 0}</p>
          <p className="text-gray-400 text-sm">Avg Messages/Trial</p>
        </div>
      </div>
      
      {/* Popular Trial Avatars */}
      <div className="bg-slate-800/50 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">
          <Bot className="w-5 h-5 inline mr-2 text-purple-400" />
          Most Used Trial Avatars
        </h3>
        <div className="space-y-3">
          {trialStats?.popular_trial_avatars?.map((avatar, idx) => (
            <div key={idx} className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center">
                  {idx + 1}
                </span>
                <p className="text-white font-medium">{avatar.name}</p>
              </div>
              <div className="text-right">
                <p className="text-green-400 font-bold">{avatar.trials}</p>
                <p className="text-gray-400 text-xs">trials</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Trial Settings Info */}
      <div className="bg-slate-800/50 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">
          <Settings className="w-5 h-5 inline mr-2" />
          Free Trial Configuration
        </h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-slate-900/50 rounded-lg p-4">
            <p className="text-gray-400 text-sm">Messages per Trial</p>
            <p className="text-2xl font-bold text-white">3</p>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-4">
            <p className="text-gray-400 text-sm">Voice Enabled</p>
            <p className="text-2xl font-bold text-green-400">Yes</p>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-4">
            <p className="text-gray-400 text-sm">Trial Status</p>
            <p className="text-2xl font-bold text-green-400">Active</p>
          </div>
        </div>
      </div>
    </div>
  );
  
  // ============ PANCHANG STATS TAB ============
  const PanchangStatsTab = () => (
    <div className="space-y-6" data-testid="panchang-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-orange-600/30 to-amber-600/30 rounded-xl p-4 border border-orange-500/30">
          <Calendar className="w-8 h-8 text-orange-400 mb-2" />
          <p className="text-2xl font-bold text-white">{panchangStats?.daily_views || 0}</p>
          <p className="text-gray-400 text-sm">Panchang Views</p>
        </div>
        <div className="bg-gradient-to-br from-purple-600/30 to-pink-600/30 rounded-xl p-4 border border-purple-500/30">
          <Star className="w-8 h-8 text-purple-400 mb-2" />
          <p className="text-2xl font-bold text-white">{panchangStats?.horoscope_views || 0}</p>
          <p className="text-gray-400 text-sm">Horoscope Views</p>
        </div>
        <div className="bg-gradient-to-br from-green-600/30 to-emerald-600/30 rounded-xl p-4 border border-green-500/30">
          <Share2 className="w-8 h-8 text-green-400 mb-2" />
          <p className="text-2xl font-bold text-white">{panchangStats?.whatsapp_shares || 0}</p>
          <p className="text-gray-400 text-sm">WhatsApp Shares</p>
        </div>
        <div className="bg-gradient-to-br from-blue-600/30 to-cyan-600/30 rounded-xl p-4 border border-blue-500/30">
          <Users className="w-8 h-8 text-blue-400 mb-2" />
          <p className="text-2xl font-bold text-white">12</p>
          <p className="text-gray-400 text-sm">Zodiac Signs</p>
        </div>
      </div>
      
      {/* Popular Zodiac Signs */}
      <div className="bg-slate-800/50 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">
          <Star className="w-5 h-5 inline mr-2 text-amber-400" />
          Most Viewed Zodiac Signs
        </h3>
        <div className="flex flex-wrap gap-3">
          {panchangStats?.popular_signs?.map((sign, idx) => (
            <div key={idx} className="bg-gradient-to-r from-purple-600/30 to-pink-600/30 px-4 py-2 rounded-full border border-purple-500/30">
              <span className="text-white font-medium">{sign}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Features Info */}
      <div className="bg-slate-800/50 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">Panchang Features</h3>
        <div className="grid md:grid-cols-2 gap-4">
          {[
            { name: 'Tithi', status: 'Active' },
            { name: 'Nakshatra', status: 'Active' },
            { name: 'Yoga', status: 'Active' },
            { name: 'Karana', status: 'Active' },
            { name: 'Sunrise/Sunset', status: 'Active' },
            { name: 'Rahu Kaal', status: 'Active' },
            { name: 'Daily Horoscope', status: 'Active' },
            { name: 'WhatsApp Share', status: 'Active' }
          ].map((feature, idx) => (
            <div key={idx} className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3">
              <span className="text-white">{feature.name}</span>
              <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">{feature.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
  
  // ============ REFERRALS TAB ============
  const ReferralsTab = () => (
    <div className="space-y-6" data-testid="referrals-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-600/30 to-emerald-600/30 rounded-xl p-4 border border-green-500/30">
          <Share2 className="w-8 h-8 text-green-400 mb-2" />
          <p className="text-2xl font-bold text-white">{referralStats?.total_referrals || 0}</p>
          <p className="text-gray-400 text-sm">Total Referrals</p>
        </div>
        <div className="bg-gradient-to-br from-amber-600/30 to-orange-600/30 rounded-xl p-4 border border-amber-500/30">
          <Check className="w-8 h-8 text-amber-400 mb-2" />
          <p className="text-2xl font-bold text-white">{referralStats?.successful_conversions || 0}</p>
          <p className="text-gray-400 text-sm">Conversions</p>
        </div>
        <div className="bg-gradient-to-br from-purple-600/30 to-pink-600/30 rounded-xl p-4 border border-purple-500/30">
          <IndianRupee className="w-8 h-8 text-purple-400 mb-2" />
          <p className="text-2xl font-bold text-white">₹{referralStats?.total_rewards_distributed || 0}</p>
          <p className="text-gray-400 text-sm">Rewards Distributed</p>
        </div>
        <div className="bg-gradient-to-br from-blue-600/30 to-cyan-600/30 rounded-xl p-4 border border-blue-500/30">
          <TrendingUp className="w-8 h-8 text-blue-400 mb-2" />
          <p className="text-2xl font-bold text-white">{referralStats?.total_referrals > 0 ? Math.round((referralStats?.successful_conversions / referralStats?.total_referrals) * 100) : 0}%</p>
          <p className="text-gray-400 text-sm">Success Rate</p>
        </div>
      </div>
      
      {/* Top Referrers */}
      <div className="bg-slate-800/50 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">
          <Crown className="w-5 h-5 inline mr-2 text-amber-400" />
          Top Referrers
        </h3>
        <div className="space-y-3">
          {referralStats?.top_referrers?.map((referrer, idx) => (
            <div key={idx} className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3">
              <div className="flex items-center gap-3">
                <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                  idx === 0 ? 'bg-amber-500 text-black' : 
                  idx === 1 ? 'bg-gray-400 text-black' : 
                  idx === 2 ? 'bg-amber-700 text-white' : 'bg-slate-700 text-white'
                }`}>
                  {idx + 1}
                </span>
                <p className="text-white font-medium">{referrer.name}</p>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <p className="text-white font-bold">{referrer.referrals}</p>
                  <p className="text-gray-400 text-xs">referrals</p>
                </div>
                <div className="text-right">
                  <p className="text-green-400 font-bold">₹{referrer.rewards}</p>
                  <p className="text-gray-400 text-xs">earned</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Referral Program Info */}
      <div className="bg-slate-800/50 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">Referral Program Settings</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-slate-900/50 rounded-lg p-4">
            <p className="text-gray-400 text-sm">Reward per Referral</p>
            <p className="text-2xl font-bold text-green-400">₹50</p>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-4">
            <p className="text-gray-400 text-sm">Program Status</p>
            <p className="text-2xl font-bold text-green-400">Active</p>
          </div>
        </div>
      </div>
    </div>
  );
  
  // ============ NOTIFICATIONS TAB ============
  const NotificationsTab = () => (
    <div className="space-y-6" data-testid="notifications-tab">
      <div className="bg-slate-800/50 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">
            <Bell className="w-5 h-5 inline mr-2 text-amber-400" />
            Notification Management
          </h3>
          <button className="px-4 py-2 bg-amber-500 text-black rounded-lg font-medium hover:bg-amber-400 transition-all">
            + Send Notification
          </button>
        </div>
        
        {/* Notification Types */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="bg-slate-900/50 rounded-lg p-4">
            <Bell className="w-8 h-8 text-blue-400 mb-2" />
            <p className="text-white font-bold">In-App</p>
            <p className="text-gray-400 text-sm">Push notifications within app</p>
            <span className="inline-block mt-2 px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">Active</span>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-4">
            <Share2 className="w-8 h-8 text-green-400 mb-2" />
            <p className="text-white font-bold">WhatsApp</p>
            <p className="text-gray-400 text-sm">Pass approval notifications</p>
            <span className="inline-block mt-2 px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">Active</span>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-4">
            <Volume2 className="w-8 h-8 text-purple-400 mb-2" />
            <p className="text-white font-bold">Browser Push</p>
            <p className="text-gray-400 text-sm">Desktop notifications</p>
            <span className="inline-block mt-2 px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">Active</span>
          </div>
        </div>
        
        {/* Recent Notifications */}
        <h4 className="text-white font-medium mb-3">Recent Notifications Sent</h4>
        <div className="space-y-3">
          {notifications.length > 0 ? notifications.map((notif, idx) => (
            <div key={idx} className="bg-slate-900/50 rounded-lg p-3 flex items-center justify-between">
              <div>
                <p className="text-white font-medium">{notif.title}</p>
                <p className="text-gray-400 text-sm">{notif.message}</p>
              </div>
              <span className="text-gray-500 text-xs">{notif.sent_at}</span>
            </div>
          )) : (
            <p className="text-gray-400 text-center py-4">No notifications sent yet</p>
          )}
        </div>
      </div>
    </div>
  );
  
  // ============ TELEGRAM TAB (100% FREE - Auto Notifications!) ============
  const TelegramTab = () => {
    const [telegramStatus, setTelegramStatus] = useState(null);
    const [testType, setTestType] = useState('pass_purchase');
    const [sending, setSending] = useState(false);
    
    useEffect(() => {
      fetchTelegramStatus();
    }, []);
    
    const fetchTelegramStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/telegram/status`);
        const data = await res.json();
        setTelegramStatus(data);
      } catch (error) {
        console.error('Error fetching Telegram status:', error);
      }
    };
    
    const sendTestNotification = async () => {
      setSending(true);
      try {
        const res = await fetch(`${API_URL}/api/telegram/test-notification?notification_type=${testType}`, {
          method: 'POST'
        });
        const data = await res.json();
        if (data.success) {
          toast.success('Test notification sent to Telegram!');
        } else {
          toast.error(data.error || 'Failed to send');
        }
      } catch (error) {
        toast.error('Failed to send test');
      }
      setSending(false);
    };
    
    return (
      <div className="space-y-6" data-testid="telegram-tab">
        {/* Status Card */}
        <div className={`rounded-xl p-6 ${telegramStatus?.configured 
          ? 'bg-gradient-to-br from-blue-600/30 to-cyan-600/30 border border-blue-500/30' 
          : 'bg-gradient-to-br from-amber-600/30 to-orange-600/30 border border-amber-500/30'}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <MessageCircle className={`w-8 h-8 ${telegramStatus?.configured ? 'text-blue-400' : 'text-amber-400'}`} />
              <div>
                <h3 className="text-lg font-bold text-white">Telegram Bot</h3>
                <p className="text-gray-400 text-sm">
                  {telegramStatus?.bot_info?.username ? `@${telegramStatus.bot_info.username}` : 'Not configured'} • <span className="text-green-400">100% FREE!</span>
                </p>
              </div>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-bold ${
              telegramStatus?.configured 
                ? 'bg-green-500/20 text-green-400' 
                : 'bg-amber-500/20 text-amber-400'
            }`}>
              {telegramStatus?.configured ? '✓ Active' : '⚠ Setup Required'}
            </span>
          </div>
          {telegramStatus?.configured && telegramStatus?.bot_info && (
            <p className="text-gray-300 text-sm">
              Bot: <strong>{telegramStatus.bot_info.first_name}</strong> • Chat ID: <code>{telegramStatus.admin_chat_id}</code>
            </p>
          )}
        </div>
        
        {/* Features */}
        <div className="bg-slate-800/50 rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">✨ Features (All FREE!)</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { name: 'Auto Notifications', icon: '🔔', desc: 'Instant alerts on your phone' },
              { name: 'Approve via Reply', icon: '✅', desc: 'Just type /approve [ID]' },
              { name: 'Reject via Reply', icon: '❌', desc: 'Just type /reject [ID]' },
              { name: 'View Pending', icon: '⏳', desc: 'Type /pending to see all' },
              { name: 'Daily Summary', icon: '📊', desc: 'Get daily business report' },
              { name: 'Unlimited Messages', icon: '♾️', desc: 'No limits, no cost!' }
            ].map((feature, idx) => (
              <div key={idx} className="bg-slate-900/50 rounded-lg p-4 flex items-start gap-3">
                <span className="text-2xl">{feature.icon}</span>
                <div>
                  <p className="text-white font-medium">{feature.name}</p>
                  <p className="text-gray-400 text-xs">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Setup Instructions */}
        {!telegramStatus?.configured && (
          <div className="bg-slate-800/50 rounded-xl p-6">
            <h3 className="text-lg font-bold text-white mb-4">🚀 Quick Setup (5 minutes)</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">1</span>
                <div>
                  <p className="text-white font-medium">Create Bot</p>
                  <p className="text-gray-400 text-sm">Open Telegram → Search <code>@BotFather</code> → Send <code>/newbot</code></p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">2</span>
                <div>
                  <p className="text-white font-medium">Get Bot Token</p>
                  <p className="text-gray-400 text-sm">BotFather will give you a token like: <code>123456789:ABCdefGHI...</code></p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">3</span>
                <div>
                  <p className="text-white font-medium">Get Your Chat ID</p>
                  <p className="text-gray-400 text-sm">Message your bot → Check API → Copy your chat ID number</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">4</span>
                <div>
                  <p className="text-white font-medium">Add to Environment</p>
                  <p className="text-gray-400 text-sm">Add these to your <code>.env</code> file:</p>
                  <div className="bg-slate-900 rounded-lg p-3 mt-2 font-mono text-sm">
                    <p className="text-green-400">TELEGRAM_BOT_TOKEN=your_bot_token</p>
                    <p className="text-green-400">TELEGRAM_ADMIN_CHAT_ID=your_chat_id</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Test Notifications */}
        {telegramStatus?.configured && (
          <div className="bg-slate-800/50 rounded-xl p-6">
            <h3 className="text-lg font-bold text-white mb-4">🧪 Send Test Notification</h3>
            <div className="flex flex-wrap gap-4">
              <select 
                value={testType} 
                onChange={(e) => setTestType(e.target.value)}
                className="bg-slate-900 text-white px-4 py-2 rounded-lg"
              >
                <option value="pass_purchase">Pass Purchase</option>
                <option value="product_order">Product Order</option>
                <option value="fund_request">Fund Request</option>
                <option value="new_registration">New Registration</option>
                <option value="daily_summary">Daily Summary</option>
                <option value="welcome">Welcome Message</option>
              </select>
              <button 
                onClick={sendTestNotification}
                disabled={sending}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-400 transition-all disabled:opacity-50"
              >
                {sending ? 'Sending...' : 'Send Test'}
              </button>
            </div>
          </div>
        )}
        
        {/* Commands Reference */}
        <div className="bg-slate-800/50 rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">📋 Bot Commands</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { cmd: '/approve ABC123', desc: 'Approve request with ID ABC123' },
              { cmd: '/reject ABC123', desc: 'Reject request with ID ABC123' },
              { cmd: '/pending', desc: 'View all pending approvals' },
              { cmd: '/summary', desc: 'Get today\'s business summary' },
              { cmd: '/status', desc: 'Check if bot is working' },
              { cmd: '/help', desc: 'Show all commands' }
            ].map((item, idx) => (
              <div key={idx} className="bg-slate-900/50 rounded-lg p-3">
                <code className="text-blue-400">{item.cmd}</code>
                <p className="text-gray-400 text-sm mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
        
        {/* Example Message */}
        <div className="bg-slate-800/50 rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">📱 Example Notification</h3>
          <div className="bg-slate-900/50 rounded-lg p-4 font-mono text-sm">
            <p className="text-blue-400">🎫 <b>PASS PURCHASE REQUEST</b></p>
            <p className="text-gray-400">━━━━━━━━━━━━━━━</p>
            <p className="text-white"><b>ID:</b> <code>ABC12345</code></p>
            <p className="text-white"><b>User:</b> Anil Kumar</p>
            <p className="text-white"><b>Pass:</b> GOLDEN</p>
            <p className="text-white"><b>Amount:</b> ₹150</p>
            <p className="text-gray-400">━━━━━━━━━━━━━━━</p>
            <p className="text-gray-300 mt-2">Reply with:</p>
            <p className="text-green-400">✅ <code>/approve ABC12345</code></p>
            <p className="text-red-400">❌ <code>/reject ABC12345</code></p>
          </div>
        </div>
      </div>
    );
  };
  
  // ============ WHATSAPP TAB (FREE - No API needed!) ============
  const WhatsAppTab = () => {
    const [whatsappStatus, setWhatsappStatus] = useState(null);
    const [testType, setTestType] = useState('pass_purchase');
    const [generatedLink, setGeneratedLink] = useState(null);
    
    useEffect(() => {
      fetchWhatsAppStatus();
    }, []);
    
    const fetchWhatsAppStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/whatsapp-business/status`);
        const data = await res.json();
        setWhatsappStatus(data);
      } catch (error) {
        console.error('Error fetching WhatsApp status:', error);
      }
    };
    
    const generateTestNotification = async () => {
      try {
        const testDetails = {
          pass_purchase: { user_name: 'Test User', pass_type: 'Golden', amount: 150, payment_ref: 'TEST123' },
          product_order: { customer_name: 'Test Customer', product_name: 'Test Product', quantity: 1, amount: 500 },
          fund_request: { user_name: 'Test User', amount: 1000, request_type: 'Withdrawal' },
          new_registration: { user_name: 'New User', email: 'test@example.com', phone: '9876543210' }
        };
        
        const res = await fetch(`${API_URL}/api/whatsapp-business/generate-notification`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            notification_type: testType,
            request_id: 'TEST' + Date.now(),
            details: testDetails[testType]
          })
        });
        const data = await res.json();
        if (data.success) {
          setGeneratedLink(data);
          toast.success('WhatsApp link generated! Click to open');
        }
      } catch (error) {
        toast.error('Failed to generate link');
      }
    };
    
    const openWhatsApp = (link) => {
      window.open(link, '_blank');
    };
    
    return (
      <div className="space-y-6" data-testid="whatsapp-tab">
        {/* Status Card - Always Green (FREE!) */}
        <div className="rounded-xl p-6 bg-gradient-to-br from-green-600/30 to-emerald-600/30 border border-green-500/30">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Share2 className="w-8 h-8 text-green-400" />
              <div>
                <h3 className="text-lg font-bold text-white">WhatsApp Notifications</h3>
                <p className="text-gray-400 text-sm">
                  Admin: {whatsappStatus?.admin_number || '919579369830'} • <span className="text-green-400">FREE - No API needed!</span>
                </p>
              </div>
            </div>
            <span className="px-3 py-1 rounded-full text-sm font-bold bg-green-500/20 text-green-400">
              ✓ Ready to Use
            </span>
          </div>
          <p className="text-gray-300 text-sm">
            {whatsappStatus?.method || 'Uses wa.me links to open WhatsApp with pre-filled messages'}
          </p>
        </div>
        
        {/* How It Works */}
        <div className="bg-slate-800/50 rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">📱 How It Works</h3>
          <div className="space-y-3">
            {(whatsappStatus?.how_it_works || [
              "1. When a request comes, you see it in Admin Panel",
              "2. Click 'Send to WhatsApp' button",
              "3. WhatsApp opens with pre-filled message",
              "4. Send to yourself as a reminder",
              "5. Approve/Reject from Admin Panel"
            ]).map((step, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <span className="w-6 h-6 bg-green-500 text-black rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                  {idx + 1}
                </span>
                <p className="text-gray-300">{step.replace(/^\d+\.\s*/, '')}</p>
              </div>
            ))}
          </div>
        </div>
        
        {/* Notification Events */}
        <div className="bg-slate-800/50 rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">Notification Events</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { name: 'Fund Requests', icon: '💰', enabled: true, desc: 'Withdrawal & fund approvals' },
              { name: 'Product Orders', icon: '📦', enabled: true, desc: 'New order notifications' },
              { name: 'Pass Purchases', icon: '🎫', enabled: true, desc: 'Consultation pass requests' },
              { name: 'New Registrations', icon: '👤', enabled: true, desc: 'New user sign-ups' },
              { name: 'Daily Summary', icon: '📊', enabled: true, desc: 'End of day business report' },
              { name: 'Manual Approval', icon: '✋', enabled: true, desc: 'Approve from Admin Panel' }
            ].map((feature, idx) => (
              <div key={idx} className="bg-slate-900/50 rounded-lg p-4 flex items-start gap-3">
                <span className="text-2xl">{feature.icon}</span>
                <div>
                  <p className="text-white font-medium">{feature.name}</p>
                  <p className="text-gray-400 text-xs">{feature.desc}</p>
                  <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs ${
                    feature.enabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                  }`}>
                    {feature.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Test Notification Generator */}
        <div className="bg-slate-800/50 rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">🧪 Test WhatsApp Notification</h3>
          <div className="flex flex-wrap gap-4 mb-4">
            <select 
              value={testType} 
              onChange={(e) => { setTestType(e.target.value); setGeneratedLink(null); }}
              className="bg-slate-900 text-white px-4 py-2 rounded-lg"
            >
              <option value="pass_purchase">Pass Purchase</option>
              <option value="product_order">Product Order</option>
              <option value="fund_request">Fund Request</option>
              <option value="new_registration">New Registration</option>
            </select>
            <button 
              onClick={generateTestNotification}
              className="px-6 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-400 transition-all"
            >
              Generate Link
            </button>
          </div>
          
          {/* Generated Link */}
          {generatedLink && (
            <div className="bg-slate-900/50 rounded-lg p-4">
              <p className="text-gray-400 text-sm mb-2">Message Preview:</p>
              <pre className="text-green-400 text-sm whitespace-pre-wrap mb-4 font-mono">
                {generatedLink.message}
              </pre>
              <button 
                onClick={() => openWhatsApp(generatedLink.whatsapp_link)}
                className="px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-500 transition-all flex items-center gap-2"
              >
                <Share2 className="w-5 h-5" />
                Open in WhatsApp
              </button>
            </div>
          )}
        </div>
        
        {/* Example Message */}
        <div className="bg-slate-800/50 rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">📋 Example Notification Message</h3>
          <div className="bg-slate-900/50 rounded-lg p-4 font-mono text-sm text-green-400">
            🎫 *PASS PURCHASE REQUEST*<br/>
            ━━━━━━━━━━━━━━━<br/>
            *ID:* ABC12345<br/>
            *User:* John Doe<br/>
            *Pass:* GOLDEN<br/>
            *Amount:* ₹150<br/>
            *Payment Ref:* UPI123<br/>
            *Time:* 24 Feb 2026, 02:30 PM<br/>
            ━━━━━━━━━━━━━━━<br/>
            <br/>
            Please approve/reject from Admin Panel
          </div>
          <p className="text-gray-400 text-sm mt-4">
            💡 <strong>Tip:</strong> When you receive this on WhatsApp, simply open your Admin Panel and approve/reject the request from there!
          </p>
        </div>
      </div>
    );
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 p-4 md:p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="w-8 h-8 text-amber-400" />
          <h1 className="text-2xl font-bold text-white">Sankat Mochan Admin</h1>
        </div>
        <p className="text-gray-400">Manage AI avatars, passes, and consultation settings</p>
      </div>
      
      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-4 mb-6">
        {[
          { id: 'overview', label: 'Overview', icon: Eye },
          { id: 'analytics', label: 'Analytics', icon: BarChart3 },
          { id: 'avatars', label: 'Avatars', icon: Bot },
          { id: 'knowledge', label: 'Knowledge Base', icon: Database },
          { id: 'passes', label: 'Passes', icon: Crown },
          { id: 'coupons', label: 'Coupons', icon: Gift },
          { id: 'trials', label: 'Free Trials', icon: Zap },
          { id: 'sessions', label: 'Sessions', icon: MessageCircle },
          { id: 'reports', label: 'Reports', icon: FileText },
          { id: 'panchang', label: 'Panchang Stats', icon: Calendar },
          { id: 'referrals', label: 'Referrals', icon: Share2 },
          { id: 'notifications', label: 'Notifications', icon: Bell },
          { id: 'telegram', label: 'Telegram Bot', icon: MessageCircle },
          { id: 'whatsapp', label: 'WhatsApp', icon: Share2 },
          { id: 'voice', label: 'Voice Settings', icon: Volume2 },
          { id: 'dvc', label: 'DVC Templates', icon: Folder },
          { id: 'settings', label: 'Settings', icon: Settings },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`admin-tab-${tab.id}`}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all
              ${activeTab === tab.id 
                ? 'bg-amber-500 text-black' 
                : 'bg-slate-800 text-white hover:bg-slate-700'}
            `}
          >
            <tab.icon className="w-5 h-5" />
            {tab.label}
          </button>
        ))}
      </div>
      
      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab />}
      {activeTab === 'analytics' && <AnalyticsTab />}
      {activeTab === 'avatars' && <AvatarsTab />}
      {activeTab === 'knowledge' && <KnowledgeBaseTab />}
      {activeTab === 'passes' && <PassesTab />}
      {activeTab === 'coupons' && <CouponsTab />}
      {activeTab === 'trials' && <FreeTrialsTab />}
      {activeTab === 'voice' && <VoiceSettingsTab />}
      {activeTab === 'dvc' && <DVCTemplatesTab />}
      {activeTab === 'panchang' && <PanchangStatsTab />}
      {activeTab === 'referrals' && <ReferralsTab />}
      {activeTab === 'notifications' && <NotificationsTab />}
      {activeTab === 'telegram' && <TelegramTab />}
      {activeTab === 'whatsapp' && <WhatsAppTab />}
      {activeTab === 'sessions' && (
        <div className="bg-slate-800/50 rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">Consultation Sessions ({sessions.length})</h3>
          <div className="space-y-3">
            {sessions.map(session => (
              <div key={session.id} className="bg-slate-900/50 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">{session.avatar_name || 'Unknown Avatar'}</p>
                  <p className="text-gray-400 text-sm">Client: {session.client_name || 'Not provided'}</p>
                  <p className="text-gray-500 text-xs">{new Date(session.started_at).toLocaleString()}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm ${
                  session.status === 'active' ? 'bg-green-500/20 text-green-400' :
                  session.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-gray-500/20 text-gray-400'
                }`}>
                  {session.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {activeTab === 'reports' && (
        <div className="bg-slate-800/50 rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">Generated Reports ({reports.length})</h3>
          <div className="space-y-3">
            {reports.map(report => (
              <div key={report.id} className="bg-slate-900/50 rounded-lg p-4">
                <p className="text-white font-medium">{report.title}</p>
                <p className="text-gray-400 text-sm capitalize">{report.report_type?.replace(/_/g, ' ')}</p>
                <p className="text-gray-500 text-xs">{new Date(report.created_at).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {activeTab === 'settings' && <SettingsTab />}
      
      {/* Create Coupon Modal */}
      {showCouponModal && <CreateCouponModal />}
    </div>
  );
};

export default SankatMochanAdmin;
